import { useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Copy, Mic2 } from 'lucide-react';

/**
 * LyricsSection - renders song lyrics with a Show/Hide toggle and a Copy button.
 *
 * Preserves original formatting:
 *  - Section labels ([Verse], [Hook], [Chorus], [Bridge], [Outro], [Intro], [Pre-Chorus])
 *    are auto-detected on any line and shown as styled pill headers
 *  - Blank lines become vertical spacing
 *  - Line breaks preserved via whitespace-pre-wrap
 *
 * Mobile-optimized with generous line-height for readability.
 */
export default function LyricsSection({ lyrics, songTitle }) {
  const [expanded, setExpanded] = useState(false);

  // Parse lyrics into structured blocks
  const blocks = useMemo(() => parseLyrics(lyrics || ''), [lyrics]);

  if (!lyrics || !lyrics.trim()) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(lyrics.trim());
      toast.success('Lyrics copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Card className="glass-card border-border p-5 sm:p-6" data-testid="lyrics-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30 flex items-center justify-center">
            <Mic2 className="w-4 h-4 text-violet-400" />
          </div>
          <h3 className="font-bold text-base sm:text-lg">Lyrics</h3>
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="gap-1.5 h-8 text-xs"
              data-testid="lyrics-copy-btn"
            >
              <Copy className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Copy</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(e => !e)}
            className="gap-1.5 h-8 text-xs font-semibold text-violet-400 hover:text-violet-300"
            data-testid="lyrics-toggle-btn"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                Hide Lyrics
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                Show Lyrics
              </>
            )}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 space-y-5 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="lyrics-content">
          {blocks.map((block, idx) => (
            <div key={idx} className="space-y-2">
              {block.label && (
                <span
                  className="inline-block px-2.5 py-1 rounded-md text-[10px] sm:text-[11px] uppercase tracking-widest font-bold bg-violet-500/15 text-violet-300 border border-violet-500/30"
                  data-testid={`lyrics-section-label-${block.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {block.label}
                </span>
              )}
              <p
                className="whitespace-pre-wrap text-foreground/90 leading-8 sm:leading-7 text-[15px] sm:text-base font-normal"
                style={{ fontVariantLigatures: 'none' }}
              >
                {block.text}
              </p>
            </div>
          ))}
        </div>
      )}

      {expanded && (
        <p className="mt-6 text-[10px] text-muted-foreground/50 italic border-t border-border/50 pt-3">
          Lyrics provided by the artist{songTitle ? ` for "${songTitle}"` : ''}. All rights reserved.
        </p>
      )}
    </Card>
  );
}

// ─── Helpers ─────────────────────────────────────────────

const SECTION_RE = /^\s*[\[\(]\s*(Intro|Verse(?:\s*\d*)?|Pre[-\s]?Chorus|Chorus|Hook|Bridge|Refrain|Break|Interlude|Outro|Post[-\s]?Chorus|Drop|Ad[-\s]?Libs?)\b[^\]\)]*[\]\)]\s*$/i;

function parseLyrics(raw) {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let current = { label: null, text: '' };
  const push = () => {
    if (current.text.trim() || current.label) {
      blocks.push({ ...current, text: current.text.replace(/^\n+|\n+$/g, '') });
    }
  };
  for (const line of lines) {
    const m = line.match(SECTION_RE);
    if (m) {
      push();
      current = { label: m[1].replace(/\s+/g, ' ').trim(), text: '' };
    } else {
      current.text += (current.text ? '\n' : '') + line;
    }
  }
  push();
  // If no section labels detected, just return one block with the whole text
  if (!blocks.length) return [{ label: null, text: raw.trim() }];
  return blocks;
}
